import type { MultiPolygon, Polygon } from "geojson";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";

import { SHOW_TEST_WARNINGS } from "@/config/test-warnings";

import type {
  RiskPictureDay,
  RiskPictureItem,
  RiskPictureKind,
  RiskPictureLevel,
  RiskPictureModel,
} from "./risk-picture-model";
import { riskPicturePhenomena } from "./risk-picture-phenomena";

const DEFAULT_DAY_COUNT = 8;
const DEFAULT_TIME_ZONE = "Europe/Amsterdam";

const KIND_BY_WARNING_TYPE: Record<string, RiskPictureKind> = {
  warning: "issued",
  "potentially-dangerous-weather": "chance",
};

const LEVELS: readonly RiskPictureLevel[] = ["yellow", "orange", "red"];

/**
 * `part.color` wins. These downloads leave it null and put the code on
 * `severity`: Moderate/Minor is code geel, Severe is oranje, Extreme is rood.
 */
const LEVEL_BY_SEVERITY: Record<string, RiskPictureLevel> = {
  minor: "yellow",
  moderate: "yellow",
  severe: "orange",
  extreme: "red",
};

interface BuildRiskPictureOptions {
  now: Date;
  dayCount?: number;
  timeZone?: string;
  /** Also show `operational_status: "Test"` sets. Defaults to the env flag. */
  includeTestWarnings?: boolean;
}

/** `Actual` always shows; `Test` only when enabled. `Exercise`, `System`, etc. never do. */
const isShownOperationalStatus = (value: unknown, includeTestWarnings: boolean) =>
  value === "Actual" || (includeTestWarnings && value === "Test");

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isAreaGeometry = (value: unknown): value is Polygon | MultiPolygon =>
  isRecord(value) &&
  (value.type === "Polygon" || value.type === "MultiPolygon") &&
  Array.isArray(value.coordinates);

const asLevel = (value: unknown): RiskPictureLevel | undefined =>
  LEVELS.find((level) => level === value);

const levelFromPart = (part: UnknownRecord): RiskPictureLevel | undefined => {
  const color = asLevel(part.color);
  if (color) return color;
  if (typeof part.severity !== "string") return undefined;
  return LEVEL_BY_SEVERITY[part.severity.toLowerCase()];
};

const dutchText = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  if (text.length === 0 || text === "-") return undefined;
  return text;
};

/**
 * Snapshot parts use `{ nl, en }` under `area description`. Other downloads use a
 * string `area_description`, which is shown in either locale.
 */
const areaTexts = (value: unknown): RiskPictureItem["areaDescription"] | undefined => {
  if (isRecord(value)) {
    const nl = dutchText(value.nl);
    const en = dutchText(value.en);
    if (!nl && !en) return undefined;
    return { ...(nl ? { nl } : {}), ...(en ? { en } : {}) };
  }
  const text = dutchText(value);
  if (!text) return undefined;
  return { nl: text, en: text };
};

const dutchTexts = (
  set: UnknownRecord,
): Pick<RiskPictureItem, "headline" | "description" | "detail"> => {
  if (!isRecord(set.texts) || !isRecord(set.texts.nl)) return {};
  const nl = set.texts.nl;
  return {
    headline: dutchText(nl.headline),
    description: dutchText(nl.description),
    detail: dutchText(nl.detailed_description),
  };
};

const asTime = (value: unknown): number | undefined => {
  if (typeof value !== "string") return undefined;
  const time = Date.parse(value);
  return Number.isNaN(time) ? undefined : time;
};

/** YYYY-MM-DD for `dayCount` consecutive local days starting at the local date of `now`. */
const localDates = (now: Date, dayCount: number, timeZone: string): string[] => {
  const [year, month, day] = formatInTimeZone(now, timeZone, "yyyy-MM-dd")
    .split("-")
    .map(Number);

  return Array.from({ length: dayCount }, (_, offset) =>
    new Date(Date.UTC(year, month - 1, day + offset)).toISOString().slice(0, 10),
  );
};

const localDayStart = (date: string, timeZone: string): number =>
  zonedTimeToUtc(`${date}T00:00:00`, timeZone).getTime();

interface PlacedItem {
  item: RiskPictureItem;
  onset: number;
  expires: number;
}

const itemsFromWarningSet =
  (includeTestWarnings: boolean) =>
  (set: unknown): PlacedItem[] => {
    if (!isRecord(set)) return [];
    if (set.status !== "active") return [];
    if (!isShownOperationalStatus(set.operational_status, includeTestWarnings)) return [];
    if (typeof set.phenomenon !== "string") return [];

    const kind =
      typeof set.warning_type === "string"
        ? KIND_BY_WARNING_TYPE[set.warning_type]
        : undefined;
    if (!kind || !Array.isArray(set.parts)) return [];

    const phenomenon = set.phenomenon;
    const texts = dutchTexts(set);

    return set.parts.flatMap((part): PlacedItem[] => {
      if (!isRecord(part) || part.status !== "active") return [];
      if (typeof part.part_uuid !== "string" || !isAreaGeometry(part.area)) return [];

      const onset = asTime(part.onset);
      const expires = asTime(part.expires);
      if (onset === undefined || expires === undefined || expires <= onset) {
        return [];
      }

      const probabilityRange =
        typeof part.probability_percentage_range === "string"
          ? part.probability_percentage_range
          : undefined;
      const areaDescription =
        areaTexts(part["area description"]) ?? areaTexts(part.area_description);

      return [
        {
          onset,
          expires,
          item: {
            id: part.part_uuid,
            phenomenon,
            kind,
            level: levelFromPart(part),
            probabilityRange,
            ...texts,
            ...(areaDescription ? { areaDescription } : {}),
            onset: part.onset as string,
            expires: part.expires as string,
            area: { type: "Feature", geometry: part.area, properties: {} },
          },
        },
      ];
    });
  };

/** One set, a bare list, or the snapshot `{ warnings: [...] }`. */
const warningSetsIn = (payload: UnknownRecord | unknown[]): unknown[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.warnings)) return payload.warnings;
  return [payload];
};

/**
 * Turns warning sets into day columns.
 *
 * Accepts one set, a list of sets, or a snapshot `{ warnings: WarningSet[] }`.
 * Only `active` + `Actual` sets and `active` parts are shown; `Test` sets too when
 * `includeTestWarnings` (env flag `SHOW_TEST_WARNINGS`: local + acceptance). Per the PO contract,
 * elapsed end times do not hide active parts; the API status controls their visibility.
 * A part lands on every local calendar day that overlaps `[onset, expires)`, so a UTC-midnight window
 * covers two Amsterdam days.
 */
export const buildRiskPicture = (
  payload: unknown,
  {
    now,
    dayCount = DEFAULT_DAY_COUNT,
    timeZone = DEFAULT_TIME_ZONE,
    includeTestWarnings = SHOW_TEST_WARNINGS,
  }: BuildRiskPictureOptions,
): RiskPictureModel => {
  if (!isRecord(payload) && !Array.isArray(payload)) {
    throw new TypeError("Risk picture payload must be a warning set or a list of them");
  }

  const catalog = riskPicturePhenomena(formatInTimeZone(now, timeZone, "yyyy-MM-dd"));
  const placed = warningSetsIn(payload).flatMap(itemsFromWarningSet(includeTestWarnings));
  // One extra date so the last column has an end bound.
  const bounds = localDates(now, dayCount + 1, timeZone);

  const days = bounds.slice(0, dayCount).map((date, index): RiskPictureDay => {
    const start = localDayStart(date, timeZone);
    const end = localDayStart(bounds[index + 1], timeZone);

    return {
      date,
      items: placed
        .filter(({ onset, expires }) => onset < end && expires > start)
        .map(({ item }) => item),
    };
  });

  const inSeason = new Set<string>(catalog);
  const extras: string[] = [];
  for (const day of days) {
    for (const item of day.items) {
      if (!inSeason.has(item.phenomenon) && !extras.includes(item.phenomenon)) {
        extras.push(item.phenomenon);
      }
    }
  }

  return { days, phenomena: [...catalog, ...extras] };
};
